import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion, Button, Card, EmptyState, KpiCard, PageHeader, Spinner, StatusPill,
} from '../components/ui';
import { Pagination } from '../components/Pagination';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import { FraudAPI } from '../services/endpoints/fraud';
import { UsersAPI } from '../services/endpoints/admin';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { EmailSimilarityReport, SimilarEmailCluster, User, Page } from '../types/api';
import { formatCount, formatDate, relativeTime } from '../utils/format';
import { NotImplementedBanner } from './UsersPage';
import './common.css';
import './FraudPage.css';

type Tab = 'emails' | 'ips';

export function FraudPage() {
  const [tab, setTab] = useState<Tab>('emails');
  const { run, busy } = useDispatchAction();

  return (
    <div>
      <PageHeader
        eyebrow="13 — FRAUD WATCH"
        title={<>Gaming the <em>trial</em>.</>}
        lede="Two signals that one person is running many accounts: emails that normalise to the same address, and new signups from an IP that already created an account. Review here, then suspend from the user's row."
      />

      <div className="nb-tabs">
        <button onClick={() => setTab('emails')} className={`nb-tabs__btn ${tab === 'emails' ? 'is-active' : ''}`}>LOOK-ALIKE EMAILS</button>
        <button onClick={() => setTab('ips')} className={`nb-tabs__btn ${tab === 'ips' ? 'is-active' : ''}`}>REPEAT IPS</button>
      </div>

      {tab === 'emails' ? <SimilarEmailsTab run={run} scanBusy={busy} /> : <RepeatIpsTab />}
    </div>
  );
}

/* ── Tab A — look-alike emails (single scan report) ──────────────────────── */

const REASON_LABEL: Record<string, string> = {
  GMAIL_NORMALIZED: 'Gmail dot/+tag',
  PLUS_TAG: '+tag',
  DIGIT_STEM: 'Name+number',
  FUZZY_LOCALPART: 'Near-match',
};
const REASON_TONE: Record<string, 'brass' | 'ember' | 'rust'> = {
  GMAIL_NORMALIZED: 'brass',
  PLUS_TAG: 'ember',
  DIGIT_STEM: 'rust',
  FUZZY_LOCALPART: 'rust',
};

function ReasonPills({ reason }: { reason: string }) {
  const parts = reason.split(',').map(r => r.trim()).filter(Boolean);
  return (
    <span className="nb-fraud__pills">
      {parts.map(r => (
        <StatusPill key={r} tone={REASON_TONE[r] ?? 'brass'}>{REASON_LABEL[r] ?? r}</StatusPill>
      ))}
    </span>
  );
}

function SimilarEmailsTab({ run, scanBusy }: {
  run: <T,>(label: string, fn: () => Promise<T>) => Promise<T | null>;
  scanBusy: boolean;
}) {
  const [report, setReport] = useState<EmailSimilarityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      setReport(await FraudAPI.similarEmails());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runScan() {
    const fresh = await run('Run similarity scan', () => FraudAPI.runScan());
    if (fresh) setReport(fresh);
  }

  const clusters = report?.clusters ?? [];

  return (
    <div>
      <div className="nb-fraud__bar">
        <div className="nb-fraud__bar-meta">
          {report?.generatedAt
            ? <>Last scan <strong>{relativeTime(report.generatedAt)}</strong></>
            : <>No scan on record yet.</>}
        </div>
        <Button tone="ember" onClick={runScan} disabled={scanBusy}>
          {scanBusy ? 'Scanning…' : 'Run scan now'}
        </Button>
      </div>

      {error && <NotImplementedBanner endpoint="/api/admin/fraud/similar-emails" />}

      {loading && !report ? <Card><Spinner /> Loading…</Card> : error ? null : (
        <>
          <section className="nb-fraud__kpis">
            <KpiCard caption="Accounts scanned" value={formatCount(report?.totalAccountsScanned ?? 0)} />
            <KpiCard caption="Clusters found" value={formatCount(report?.totalClusters ?? 0)} />
            <KpiCard
              caption="Flagged accounts"
              value={formatCount(report?.totalFlaggedAccounts ?? 0)}
              footer={report && report.totalFlaggedAccounts > 0
                ? <StatusPill tone="ember">Needs review</StatusPill>
                : <StatusPill tone="moss">All clear</StatusPill>}
            />
            <KpiCard
              caption="Unscannable"
              value={formatCount(report?.totalUnscannable ?? 0)}
              footer={(report?.totalUnscannable ?? 0) > 0
                ? <StatusPill tone="rust">Missing/invalid email</StatusPill>
                : undefined}
            />
            <KpiCard caption="Last scan" value={report?.generatedAt ? relativeTime(report.generatedAt) : '—'} />
          </section>

          {clusters.length === 0 ? (
            <Card>
              <EmptyState
                title="No look-alike accounts"
                hint="No clusters in the latest scan. Run a scan to refresh against current signups."
              />
            </Card>
          ) : (
            <div className="nb-fraud__clusters">
              {clusters.map((c, i) => (
                <div key={c.canonical + i} className="nb-fraud__cluster" style={{ '--i': i } as React.CSSProperties}>
                  <ClusterRow cluster={c} defaultOpen={i === 0} />
                </div>
              ))}
            </div>
          )}

          {report?.unscannable && report.unscannable.length > 0 && (
            <Accordion
              title={<span className="nb-fraud__canonical">Unscannable accounts</span>}
              subtitle={`${report.unscannable.length} with a missing or invalid email`}
              badge={<StatusPill tone="rust">Review</StatusPill>}
            >
              <table className="nb-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {report.unscannable.map(a => (
                    <tr key={a.id}>
                      <td>
                        <Link to={`/users/${encodeURIComponent(a.id)}`} className="nb-table__primary nb-table__link">
                          {a.email?.trim() ? a.email : '(no email)'}
                        </Link>
                      </td>
                      <td className="nb-table__mono">{formatDate(a.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Accordion>
          )}
        </>
      )}
    </div>
  );
}

function ClusterRow({ cluster, defaultOpen }: { cluster: SimilarEmailCluster; defaultOpen: boolean }) {
  return (
    <Accordion
      defaultOpen={defaultOpen}
      title={<span className="nb-fraud__canonical">{cluster.canonical}</span>}
      subtitle={`${cluster.accounts.length} accounts`}
      badge={<ReasonPills reason={cluster.reason} />}
    >
      <table className="nb-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Joined</th>
          </tr>
        </thead>
        <tbody>
          {cluster.accounts.map(a => (
            <tr key={a.id}>
              <td>
                <Link to={`/users/${encodeURIComponent(a.id)}`} className="nb-table__primary nb-table__link">{a.email}</Link>
              </td>
              <td className="nb-table__mono">{formatDate(a.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Accordion>
  );
}

/* ── Tab B — repeat IPs (paged user list) ────────────────────────────────── */

function RepeatIpsTab() {
  const [acting, setActing] = useState<{ user: User; kind: 'suspend' | 'unsuspend' } | null>(null);
  const { run } = useDispatchAction();

  const fetcher = useCallback(async ({ page }: { page: number }): Promise<Page<User>> => {
    return FraudAPI.ipFlagged(page);
  }, []);
  const q = usePagedQuery<User, Record<string, never>>({ fetcher, initialFilters: {}, pageSize: 25 });

  async function performAction() {
    if (!acting) return;
    const { user, kind } = acting;
    await run(kind === 'suspend' ? 'Suspend user' : 'Restore user',
      () => kind === 'suspend' ? UsersAPI.suspend(user.id, 'Flagged: signup IP reuse') : UsersAPI.unsuspend(user.id, 'Admin action'));
    setActing(null);
    q.refresh();
  }

  return (
    <div>
      {q.error && <NotImplementedBanner endpoint="/api/admin/fraud/ip-flagged" />}

      {q.loading && !q.data.length ? <Card><Spinner /> Loading…</Card> :
       q.data.length === 0 ? <Card><EmptyState title="No IP-flagged accounts" hint="No signups from a reused IP yet. New collisions appear here automatically." /></Card> :
        <Card padded={false}>
          <table className="nb-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map(u => (
                <tr key={u.id}>
                  <td>
                    <Link to={`/users/${encodeURIComponent(u.id)}`} className="nb-table__primary nb-table__link">{u.email}</Link>
                    <div className="nb-table__sub"><StatusPill tone="rust">IP reuse</StatusPill></div>
                  </td>
                  <td><span className="nb-table__mono">{u.roles.join(' · ')}</span></td>
                  <td>
                    {u.disabled ? <StatusPill tone="rust">Suspended</StatusPill> :
                     u.subscription?.status === 'ACTIVE' ? <StatusPill tone="moss">Active</StatusPill> :
                     <StatusPill tone="brass">Free</StatusPill>}
                  </td>
                  <td className="nb-table__mono">{formatDate(u.createdAt)}</td>
                  <td className="nb-table__align-right">
                    <div className="nb-row-actions">
                      {u.disabled
                        ? <Button size="sm" onClick={() => setActing({ user: u, kind: 'unsuspend' })}>Restore</Button>
                        : <Button size="sm" tone="danger" onClick={() => setActing({ user: u, kind: 'suspend' })}>Suspend</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
        </Card>
      }

      <ConfirmDialog
        open={!!acting}
        title={acting?.kind === 'suspend' ? 'Suspend account' : 'Restore account'}
        message={acting?.kind === 'suspend'
          ? `Suspending ${acting?.user.email} blocks sign-in immediately. They will see a generic "account unavailable" message.`
          : `Restoring ${acting?.user.email} re-enables sign-in.`}
        requireReason
        destructive={acting?.kind === 'suspend'}
        confirmLabel={acting?.kind === 'suspend' ? 'Suspend' : 'Restore'}
        onClose={() => setActing(null)}
        onConfirm={performAction}
      />
    </div>
  );
}
